import React, { ComponentType as ReactComponent } from 'react';
import { Editor } from '@tiptap/core';
import { undoDepth, redoDepth } from 'prosemirror-history';
import {
  Button,
  ButtonGroup,
  HStack,
  Icon,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import {
  TbAlignCenter,
  TbAlignJustified,
  TbAlignLeft,
  TbAlignRight,
  TbArrowBackUp,
  TbArrowForwardUp,
  TbBlockquote,
  TbBold,
  TbCode,
  TbItalic,
  TbLink,
  TbList,
  TbListNumbers,
  TbPhoto,
  TbStrikethrough,
  TbUnderline,
  TbUnlink,
} from 'react-icons/tb';

import createTypedDefiner from '../../support/createTypedDefiner';
import { Select } from '../Select';

type Props = {
  editor: Editor | null;
  basicToolbar?: boolean;
  extraButtons?: Array<CustomButtonConfig>;
};

type ButtonConfig = {
  type: 'button';
  icon: ReactComponent;
  getLabel: () => string;
  isEnabled: (editor: Editor) => boolean;
  isActive: (editor: Editor) => boolean;
  onPress: (editor: Editor) => void;
};

export type CustomButtonConfig = {
  getLabel: () => string;
  onPress: (editor: Editor) => void;
};

type SelectOption = {
  getLabel: () => string;
  isActive: (editor: Editor) => boolean;
  onSelect: (editor: Editor) => void;
};

type SelectConfig = {
  type: 'select';
  options: Array<SelectOption>;
};

type MenuItemConfig = ButtonConfig | SelectConfig;

const defineMenuItems = createTypedDefiner<MenuItemConfig>();

const allMenuItems = defineMenuItems({
  Bold: {
    type: 'button',
    icon: TbBold,
    getLabel: () => t('Bold'),
    isEnabled: () => true,
    isActive: (editor) => editor.isActive('bold'),
    onPress: (editor) => {
      editor.chain().focus().toggleBold().run();
    },
  },
  Italic: {
    type: 'button',
    icon: TbItalic,
    getLabel: () => t('Italic'),
    isEnabled: () => true,
    isActive: (editor) => editor.isActive('italic'),
    onPress: (editor) => {
      editor.chain().focus().toggleItalic().run();
    },
  },
  Underline: {
    type: 'button',
    icon: TbUnderline,
    getLabel: () => t('Underline'),
    isEnabled: () => true,
    isActive: (editor) => editor.isActive('underline'),
    onPress: (editor) => {
      editor.chain().focus().toggleUnderline().run();
    },
  },
  Strike: {
    type: 'button',
    icon: TbStrikethrough,
    getLabel: () => t('Strikethrough'),
    isEnabled: () => true,
    isActive: (editor) => editor.isActive('strike'),
    onPress: (editor) => {
      editor.chain().focus().toggleStrike().run();
    },
  },
  AlignLeft: {
    type: 'button',
    icon: TbAlignLeft,
    getLabel: () => t('Align Left'),
    isEnabled: () => true,
    isActive: (editor) => editor.isActive({ textAlign: 'left' }),
    onPress: (editor) => {
      editor.chain().focus().setTextAlign('left').run();
    },
  },
  AlignCenter: {
    type: 'button',
    icon: TbAlignCenter,
    getLabel: () => t('Align Center'),
    isEnabled: () => true,
    isActive: (editor) => editor.isActive({ textAlign: 'center' }),
    onPress: (editor) => {
      editor.chain().focus().setTextAlign('center').run();
    },
  },
  AlignRight: {
    type: 'button',
    icon: TbAlignRight,
    getLabel: () => t('Align Right'),
    isEnabled: () => true,
    isActive: (editor) => editor.isActive({ textAlign: 'right' }),
    onPress: (editor) => {
      editor.chain().focus().setTextAlign('right').run();
    },
  },
  AlignJustify: {
    type: 'button',
    icon: TbAlignJustified,
    getLabel: () => t('Align Justified'),
    isEnabled: () => true,
    isActive: (editor) => editor.isActive({ textAlign: 'justify' }),
    onPress: (editor) => {
      editor.chain().focus().setTextAlign('justify').run();
    },
  },
  OrderedList: {
    type: 'button',
    icon: TbListNumbers,
    getLabel: () => t('Numbered List'),
    isEnabled: () => true,
    isActive: (editor) => editor.isActive('orderedList'),
    onPress: (editor) => {
      editor.chain().focus().toggleOrderedList().run();
    },
  },
  BulletList: {
    type: 'button',
    icon: TbList,
    getLabel: () => t('Bulleted List'),
    isEnabled: () => true,
    isActive: (editor) => editor.isActive('bulletList'),
    onPress: (editor) => {
      editor.chain().focus().toggleBulletList().run();
    },
  },
  Blockquote: {
    type: 'button',
    icon: TbBlockquote,
    getLabel: () => t('Blockquote'),
    isEnabled: () => true,
    isActive: (editor) => editor.isActive('blockquote'),
    onPress: (editor) => {
      editor.chain().focus().toggleBlockquote().run();
    },
  },
  Link: {
    type: 'button',
    icon: TbLink,
    getLabel: () => t('Link'),
    isEnabled: () => true,
    isActive: () => false,
    onPress: (editor) => {
      // The showLinkDialog command is defined in LinkUi.ts
      editor.commands.showLinkDialog();
    },
  },
  Unlink: {
    type: 'button',
    icon: TbUnlink,
    getLabel: () => t('Unlink'),
    isEnabled: (editor) => editor.isActive('link'),
    isActive: () => false,
    onPress: (editor) => {
      editor.chain().focus().unsetLink().run();
    },
  },
  Image: {
    type: 'button',
    icon: TbPhoto,
    getLabel: () => t('Insert Image'),
    isEnabled: () => true,
    isActive: (editor) => editor.isActive('image'),
    onPress: (editor) => {
      // TODO: Use a real dialog
      // eslint-disable-next-line no-alert
      const src = prompt('Enter the URL to an image:');
      // TODO: Validate src
      if (src) {
        editor.chain().focus().setImage({ src }).run();
      }
    },
  },
  Undo: {
    type: 'button',
    icon: TbArrowBackUp,
    getLabel: () => t('Undo'),
    isEnabled: (editor) => undoDepth(editor.state) > 0,
    isActive: () => false,
    onPress: (editor) => {
      editor.chain().focus().undo().run();
    },
  },
  Redo: {
    type: 'button',
    icon: TbArrowForwardUp,
    getLabel: () => t('Redo'),
    isEnabled: (editor) => redoDepth(editor.state) > 0,
    isActive: () => false,
    onPress: (editor) => {
      editor.chain().focus().redo().run();
    },
  },
  Source: {
    type: 'button',
    icon: TbCode,
    getLabel: () => t('View Source'),
    isEnabled: () => true,
    isActive: () => false,
    onPress: () => {},
  },
  Format: {
    type: 'select',
    options: [
      {
        getLabel: () => t('Paragraph'),
        isActive: () => false,
        onSelect: (editor) => {
          editor.chain().focus().setParagraph().run();
        },
      },
      {
        getLabel: () => t('Heading 1'),
        isActive: (editor) => editor.isActive('heading', { level: 1 }),
        onSelect: (editor) => {
          editor.chain().focus().toggleHeading({ level: 1 }).run();
        },
      },
      {
        getLabel: () => t('Heading 2'),
        isActive: (editor) => editor.isActive('heading', { level: 2 }),
        onSelect: (editor) => {
          editor.chain().focus().toggleHeading({ level: 2 }).run();
        },
      },
      {
        getLabel: () => t('Heading 3'),
        isActive: (editor) => editor.isActive('heading', { level: 3 }),
        onSelect: (editor) => {
          editor.chain().focus().toggleHeading({ level: 3 }).run();
        },
      },
      {
        getLabel: () => t('Heading 4'),
        isActive: (editor) => editor.isActive('heading', { level: 4 }),
        onSelect: (editor) => {
          editor.chain().focus().toggleHeading({ level: 4 }).run();
        },
      },
    ],
  },
});

type MenuItems = typeof allMenuItems;

type ButtonName = KeysWhere<MenuItems, 'type', 'button'>;
type SelectName = KeysWhere<MenuItems, 'type', 'select'>;

type ToolbarLayout = Array<Array<ButtonName> | SelectName>;

const BasicToolbar: ToolbarLayout = [
  ['Bold', 'Italic'],
  'Format',
  ['OrderedList', 'BulletList'],
  ['Link', 'Unlink'],
];

const NormalToolbar: ToolbarLayout = [
  ['Bold', 'Italic', 'Underline'],
  'Format',
  ['AlignLeft', 'AlignCenter', 'AlignRight', 'AlignJustify'],
  ['OrderedList', 'BulletList', 'Blockquote'],
  ['Link', 'Unlink'],
  ['Image'],
  ['Undo', 'Redo'],
  // ['Source'],
];

export default function MenuBar({ editor, basicToolbar, extraButtons }: Props) {
  if (!editor) {
    return null;
  }
  const toolbar = basicToolbar ? BasicToolbar : NormalToolbar;
  return (
    <HStack>
      {toolbar.map((item, i) => {
        return Array.isArray(item) ? (
          <MenuGroup key={i} editor={editor} group={item} />
        ) : (
          <MenuSelect key={i} editor={editor} item={allMenuItems[item]} />
        );
      })}
      {extraButtons?.map((button, i) => (
        <CustomButton key={toolbar.length + i} editor={editor} item={button} />
      ))}
    </HStack>
  );
}

type MenuGroupProps = {
  editor: Editor;
  group: Array<ButtonName>;
};

function MenuGroup({ editor, group }: MenuGroupProps) {
  return (
    <ButtonGroup isAttached variant="outline">
      {group.map((name, i) => (
        <MenuIconButton key={i} editor={editor} item={allMenuItems[name]} />
      ))}
    </ButtonGroup>
  );
}

type ButtonProps = {
  editor: Editor;
  item: ButtonConfig;
};

function MenuIconButton({ editor, item }: ButtonProps) {
  const isActive = item.isActive(editor);
  const isEnabled = item.isEnabled(editor);
  const label = item.getLabel();
  return (
    <Tooltip label={label} placement="top" hasArrow>
      <IconButton
        isActive={isActive}
        isDisabled={!isEnabled}
        aria-label={label}
        sx={{
          // This shouldn't be necessary but there seems to be a bug in Chakra 1.x
          // TODO: Remove this after we upgrade to @chakra-ui/react 2.x
          '&:not(:last-of-type)': { mr: '-1px' },
        }}
        onClick={() => {
          if (item.isEnabled(editor)) {
            item.onPress(editor);
          }
        }}
        icon={<Icon as={item.icon} fontSize={20} />}
      />
    </Tooltip>
  );
}

type CustomButtonProps = {
  editor: Editor;
  item: CustomButtonConfig;
};

function CustomButton({ editor, item }: CustomButtonProps) {
  return (
    <Button
      variant="outline"
      onClick={() => {
        item.onPress(editor);
      }}
    >
      {item.getLabel()}
    </Button>
  );
}

type SelectProps = {
  editor: Editor;
  item: SelectConfig;
};

function MenuSelect({ editor, item }: SelectProps) {
  const selectedItem = item.options.find((item) => item.isActive(editor));
  return (
    <Select
      width="auto"
      options={item.options}
      value={selectedItem}
      getLabel={(item) => item.getLabel()}
      onChange={(item) => item.onSelect(editor)}
    />
  );
}

// Some type helpers

type KeysWhere<O extends Record<string, object>, P extends string, V> = {
  [K in keyof O]: O[K] extends Record<P, unknown>
    ? O[K][P] extends V
      ? K
      : never
    : never;
}[keyof O];
